import { FC, MouseEvent, useState } from 'react'
import ReactQuill from 'react-quill'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import commonCodebase from 'common-recruspin-codebase'
import {
  Autocomplete,
  Box,
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { FormikProps } from 'formik'
import { FormattedMessage, useIntl } from 'react-intl'
import 'react-quill/dist/quill.snow.css'
import Loading from '../../../../../components/Loading'
import { Iselect, channels } from '../../../../../constants'
import { Campaign, Job } from './types'
import { filterCampaigns, filterJobs, formatJobLocation } from '../../../../../utils/jobSearch'
import { StyledCard, StyledCardContent, StyledCardHeader } from './styledComponents'

const { statuses, types } = commonCodebase.orders

interface Props {
  formik: FormikProps<any>
  order: any
  campaigns: Campaign[] | undefined
  originalMetaCampaigns: Campaign[] | undefined
  tiktokCampaigns: Campaign[] | undefined
  googleCampaigns: Campaign[] | undefined
  jobs: Job[] | undefined
  loadingCampaigns: boolean
  loadingTiktokCampaigns: boolean
  loadingGoogleCampaigns: boolean
  loadingJobs: boolean
  isCustomer: boolean | string | undefined
  permission: (field: string) => boolean
}

const JobDetailsSection: FC<Props> = ({
  formik,
  order,
  campaigns,
  originalMetaCampaigns,
  tiktokCampaigns,
  googleCampaigns,
  jobs,
  loadingCampaigns,
  loadingTiktokCampaigns,
  loadingGoogleCampaigns,
  loadingJobs,
  isCustomer,
  permission,
}) => {
  const intl = useIntl()

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [anchorElOriginal, setAnchorElOriginal] = useState<null | HTMLElement>(null)
  const openCampaign = Boolean(anchorEl)
  const openOriginalCampaign = Boolean(anchorElOriginal)

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget)
  const handleClickOriginal = (event: MouseEvent<HTMLButtonElement>) =>
    setAnchorElOriginal(event.currentTarget)
  const handleCampaignClose = () => setAnchorEl(null)
  const handleOriginalCampaignClose = () => setAnchorElOriginal(null)

  const redirectToCampaign = () => {
    handleCampaignClose()
    if (formik.values.metaCampaigns && formik.values.metaCampaigns.length > 0) {
      window.open(`/campaigns/${formik.values.metaCampaigns[0]}?legacy=1`, '_blank')
    }
  }

  const redirectToOriginalCampaign = () => {
    handleOriginalCampaignClose()
    window.open(`/analytics/dashboard/${formik.values.originalMetaCampaign}`, '_blank')
  }

  return (
    <StyledCard>
      <StyledCardHeader
        title={<FormattedMessage id="job-details" />}
        action={
          <FormControlLabel
            control={
              <Switch
                checked={formik.values.freeFeed}
                onChange={(e) => formik.setFieldValue('freeFeed', e.target.checked)}
                color="primary"
              />
            }
            label="Free Feed"
            labelPlacement="start"
          />
        }
      />
      <StyledCardContent>
        <Grid container spacing={3}>
          {permission('jobName') && (
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={intl.formatMessage({ id: 'job-title' })}
                id="jobName"
                name="jobName"
                placeholder="Job title"
                type="jobName"
                onChange={formik.handleChange}
                error={formik.touched.jobName && Boolean(formik.errors.jobName)}
                defaultValue={order?.jobName}
              />
            </Grid>
          )}

          {permission('status') && !isCustomer && (
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="status">Status</InputLabel>
                <Select
                  fullWidth
                  labelId="status"
                  id="status"
                  name="status"
                  label={intl.formatMessage({ id: 'status' })}
                  onChange={formik.handleChange}
                  defaultValue={order?.status}
                >
                  {statuses.map((i: Iselect) => (
                    <MenuItem key={i.value} value={i.value}>
                      {i.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {permission('jobType') && (
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="taskType">
                  <FormattedMessage id="order-type" />
                </InputLabel>
                <Select
                  fullWidth
                  labelId="taskType"
                  id="taskType"
                  name="taskType"
                  label={<FormattedMessage id="order-type" />}
                  onChange={formik.handleChange}
                  defaultValue={order?.jobType}
                >
                  {types.map((i: Iselect) => (
                    <MenuItem key={i.value} value={i.value}>
                      {i.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {permission('place') && (
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={<FormattedMessage id="place" />}
                id="place"
                name="place"
                placeholder="Place"
                type="place"
                onChange={formik.handleChange}
                error={formik.touched.place && Boolean(formik.errors.place)}
                value={formik.values.place}
              />
            </Grid>
          )}

          {permission('channel') && (
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="channel">
                  <FormattedMessage id="portal" />
                </InputLabel>
                <Select
                  fullWidth
                  multiple
                  labelId="channel"
                  id="channel"
                  name="channel"
                  label={<FormattedMessage id="portal" />}
                  onChange={formik.handleChange}
                  defaultValue={order?.channel}
                >
                  {channels.map((i: Iselect) => (
                    <MenuItem key={i.value} value={i.value}>
                      <Chip
                        variant="outlined"
                        key={i.value}
                        label={i.label}
                        sx={{ '& .MuiChip-label': { display: 'flex', alignItems: 'center' } }}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {permission('jobAdLink') && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={intl.formatMessage({ id: 'job-ad-link' })}
                id="jobAdLink"
                name="jobAdLink"
                placeholder="Link to job ad"
                type="text"
                onChange={formik.handleChange}
                error={formik.touched.jobAdLink && Boolean(formik.errors.jobAdLink)}
                helperText={
                  formik.touched.jobAdLink && formik.errors.jobAdLink
                    ? String(formik.errors.jobAdLink)
                    : undefined
                }
                defaultValue={order?.jobAdLink}
              />
            </Grid>
          )}

          {/* Not permission-gated: the apply link is visible to every role, like
              employmentType below. Gating it would need applyUrl added to the stored
              orderDetailsFields of every existing Permission row. */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={intl.formatMessage({ id: 'apply-link' })}
              id="applyUrl"
              name="applyUrl"
              placeholder="Link to apply"
              type="text"
              onChange={formik.handleChange}
              error={formik.touched.applyUrl && Boolean(formik.errors.applyUrl)}
              helperText={
                formik.touched.applyUrl && formik.errors.applyUrl
                  ? String(formik.errors.applyUrl)
                  : undefined
              }
              defaultValue={order?.applyUrl}
            />
          </Grid>

          {permission('keywords') && (
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={<FormattedMessage id="keywords-comma" />}
                id="keywords"
                name="keywords"
                placeholder="Keywords"
                type="text"
                onChange={formik.handleChange}
                error={formik.touched.keywords && Boolean(formik.errors.keywords)}
                defaultValue={order?.keywords}
              />
            </Grid>
          )}

          {permission('externalImportId') && !isCustomer && (
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={intl.formatMessage({ id: 'external-import-id' })}
                id="externalImportId"
                name="externalImportId"
                placeholder="externalImportId"
                type="text"
                onChange={formik.handleChange}
                error={formik.touched.externalImportId && Boolean(formik.errors.externalImportId)}
                defaultValue={order?.externalImportId}
              />
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={intl.formatMessage({ id: 'employment-type' })}
              id="employmentType"
              name="employmentType"
              placeholder="Employment type"
              type="text"
              onChange={formik.handleChange}
              error={formik.touched.employmentType && Boolean(formik.errors.employmentType)}
              defaultValue={order?.employmentType}
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ mb: 2 }}>
              <InputLabel
                sx={{
                  mb: 1,
                  color:
                    formik.touched.adDescription && formik.errors.adDescription
                      ? 'error.main'
                      : 'text.primary',
                  fontWeight: 'medium',
                }}
              >
                Ad Description
              </InputLabel>
              <ReactQuill
                value={formik.values.adDescription || ''}
                onChange={(value) => formik.setFieldValue('adDescription', value)}
                placeholder="Enter ad description..."
                modules={{
                  toolbar: [
                    [{ header: [1, 2, 3, false] }],
                    ['bold', 'italic', 'underline', 'strike'],
                    [{ list: 'ordered' }, { list: 'bullet' }],
                    ['link'],
                    ['clean'],
                  ],
                }}
                formats={[
                  'header',
                  'bold',
                  'italic',
                  'underline',
                  'strike',
                  'list',
                  'bullet',
                  'link',
                ]}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '4px',
                  border:
                    formik.touched.adDescription && formik.errors.adDescription
                      ? '2px solid #d32f2f'
                      : '1px solid #c4c4c4',
                }}
              />
              {formik.touched.adDescription && formik.errors.adDescription && (
                <Box sx={{ mt: 1, color: 'error.main', fontSize: '0.75rem' }}>
                  {typeof formik.errors.adDescription === 'string'
                    ? formik.errors.adDescription
                    : 'Invalid ad description format'}
                </Box>
              )}
            </Box>
          </Grid>

          {permission('metaCampaign') && (
            <Grid item xs={12}>
              {loadingCampaigns ? (
                <Loading />
              ) : (
                <Box display="flex" alignItems="center" gap={1}>
                  <Autocomplete
                    fullWidth
                    multiple
                    options={campaigns || []}
                    getOptionLabel={(option: Campaign) => option.name}
                    getOptionKey={(option: Campaign) => option.id}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    filterOptions={(options, { inputValue }) =>
                      filterCampaigns(options, inputValue)
                    }
                    value={
                      campaigns?.filter((c: Campaign) =>
                        formik.values.metaCampaigns?.includes(c.id)
                      ) || []
                    }
                    onChange={(e, value) => {
                      formik.setFieldValue(
                        'metaCampaigns',
                        value.map((v) => v.id)
                      )
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={<FormattedMessage id="leadgenform" />}
                        id="metaCampaigns"
                        name="metaCampaigns"
                        placeholder="Leadgenform"
                        type="text"
                        error={formik.touched.metaCampaigns && Boolean(formik.errors.metaCampaigns)}
                      />
                    )}
                  />
                  <Tooltip title="options">
                    <IconButton
                      id="basic-button"
                      aria-controls={openCampaign ? 'basic-menu' : undefined}
                      aria-haspopup="true"
                      aria-expanded={openCampaign ? 'true' : undefined}
                      onClick={handleClick}
                      disabled={!formik.values.metaCampaigns?.length}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    id="basic-menu"
                    anchorEl={anchorEl}
                    open={openCampaign}
                    onClose={handleCampaignClose}
                    MenuListProps={{ 'aria-labelledby': 'basic-button' }}
                  >
                    <MenuItem onClick={() => redirectToCampaign()}>
                      <FormattedMessage id="Open Campaign" />
                    </MenuItem>
                  </Menu>
                </Box>
              )}
            </Grid>
          )}

          {/* Customers get the field on self-service orders only, where the campaign runs
              on their own Meta account; the server lists only their own campaigns. On the
              orders we run, they could otherwise unlink the campaign we set. */}
          {(!isCustomer || order?.isSelfService) && (
            <Grid item xs={12}>
              {loadingCampaigns ? (
                <Loading />
              ) : (
                <Box display="flex" alignItems="center" gap={1}>
                  <Autocomplete
                    fullWidth
                    options={originalMetaCampaigns || []}
                    getOptionLabel={(option: Campaign) => option.name}
                    getOptionKey={(option: Campaign) => option.id}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    filterOptions={(options, { inputValue }) =>
                      filterCampaigns(options, inputValue)
                    }
                    onChange={(e, value) => {
                      formik.setFieldValue('originalMetaCampaign', value?.id)
                    }}
                    defaultValue={order?.originalMetaCampaign}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={<FormattedMessage id="original-meta-campaign" />}
                        id="originalMetaCampaign"
                        name="originalMetaCampaign"
                        placeholder="Original meta campaign"
                        type="text"
                        value={formik.values.originalMetaCampaign}
                        onChange={formik.handleChange}
                        error={
                          formik.touched.originalMetaCampaign &&
                          Boolean(formik.errors.originalMetaCampaign)
                        }
                      />
                    )}
                  />
                  {/* The campaign dashboard is internal; customers see the stats in the
                      order's analytics. */}
                  {!isCustomer && (
                    <>
                      <Tooltip title="options">
                        <IconButton
                          id="original-campaign-button"
                          aria-controls={
                            openOriginalCampaign ? 'original-campaign-menu' : undefined
                          }
                          aria-haspopup="true"
                          aria-expanded={openOriginalCampaign ? 'true' : undefined}
                          onClick={handleClickOriginal}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Tooltip>
                      <Menu
                        id="original-campaign-menu"
                        anchorEl={anchorElOriginal}
                        open={openOriginalCampaign}
                        onClose={handleOriginalCampaignClose}
                        MenuListProps={{ 'aria-labelledby': 'original-campaign-button' }}
                      >
                        <MenuItem onClick={() => redirectToOriginalCampaign()}>
                          <FormattedMessage id="Open Campaign" />
                        </MenuItem>
                      </Menu>
                    </>
                  )}
                </Box>
              )}
            </Grid>
          )}

          {!isCustomer && (
            <Grid item xs={12}>
              {loadingTiktokCampaigns ? (
                <Loading />
              ) : (
                <Autocomplete
                  fullWidth
                  options={tiktokCampaigns || []}
                  getOptionLabel={(option: Campaign) => option.name}
                  getOptionKey={(option: Campaign) => option.id}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={
                    tiktokCampaigns?.find((c: Campaign) => c.id === formik.values.tiktokCampaign) ||
                    null
                  }
                  onChange={(e, value) => {
                    formik.setFieldValue('tiktokCampaign', value?.id || '')
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="TikTok Campaign"
                      id="tiktokCampaign"
                      name="tiktokCampaign"
                      placeholder="TikTok Campaign"
                      type="text"
                      error={formik.touched.tiktokCampaign && Boolean(formik.errors.tiktokCampaign)}
                    />
                  )}
                />
              )}
            </Grid>
          )}

          {!isCustomer && (
            <Grid item xs={12}>
              {loadingGoogleCampaigns ? (
                <Loading />
              ) : (
                <Autocomplete
                  fullWidth
                  multiple
                  options={googleCampaigns || []}
                  // A campaign belongs to one order: selecting one that another order
                  // already has moves it here, so say so before it is picked.
                  getOptionLabel={(option: Campaign) =>
                    option.orderId && option.orderId !== order?.id
                      ? `${option.name} (on another order)`
                      : option.name
                  }
                  getOptionKey={(option: Campaign) => option.id}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={
                    googleCampaigns?.filter((c: Campaign) =>
                      formik.values.googleCampaigns?.includes(c.id)
                    ) || []
                  }
                  onChange={(e, value) => {
                    formik.setFieldValue(
                      'googleCampaigns',
                      value.map((v) => v.id)
                    )
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Google Campaigns"
                      id="googleCampaigns"
                      name="googleCampaigns"
                      placeholder="Google Campaigns"
                      type="text"
                      error={
                        formik.touched.googleCampaigns && Boolean(formik.errors.googleCampaigns)
                      }
                    />
                  )}
                />
              )}
            </Grid>
          )}

          {!isCustomer && (
            <Grid item xs={12}>
              {loadingJobs ? (
                <Loading />
              ) : (
                <Autocomplete
                  fullWidth
                  options={jobs || []}
                  getOptionLabel={(option: Job) => option.title}
                  getOptionKey={(option: Job) => option.id}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  filterOptions={(options, { inputValue }) => filterJobs(options, inputValue)}
                  renderOption={(props, option: Job) => {
                    const subtitle = [formatJobLocation(option.location), option.company]
                      .filter(Boolean)
                      .join(' \u00b7 ')
                    return (
                      <Box component="li" {...props} key={option.id}>
                        <Box>
                          <Typography variant="body2">{option.title}</Typography>
                          {subtitle && (
                            <Typography variant="caption" color="text.secondary">
                              {subtitle}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )
                  }}
                  value={jobs?.find((j: Job) => j.id === formik.values.atsJob) || null}
                  onChange={(e, value) => {
                    formik.setFieldValue('atsJob', value?.id || '')
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="ATS Job"
                      id="atsJob"
                      name="atsJob"
                      placeholder="ATS Job"
                      type="text"
                      error={formik.touched.atsJob && Boolean(formik.errors.atsJob)}
                    />
                  )}
                />
              )}
            </Grid>
          )}
        </Grid>
      </StyledCardContent>
    </StyledCard>
  )
}

export default JobDetailsSection
